/**
 * Shared test configuration composable
 * Provides reactive configuration state shared between App.vue and DevPanel
 */
import { ref, watch, readonly, computed } from "vue";
const STORAGE_KEY = "ai-assistant-test-config";

export interface TestConfig {
  position: "left" | "right";
  token: string;
  userId: string;
  licenseId: string;
  patientId: string;
  bookingId: string | null;
  professionalId: string;
  pms: string;
  autoStart: boolean;
}

const defaultConfig: TestConfig = {
  position: "right",
  token: process.env.VUE_APP_TEST_TOKEN || "",
  userId: "160044995",
  licenseId: "17973",
  patientId: "5",
  bookingId: null,
  professionalId: "8",
  pms: "feegow",
  autoStart: false,
};

// Load config from localStorage
function loadConfig(): TestConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultConfig, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.warn("[TestConfig] Failed to load config:", error);
  }
  return { ...defaultConfig };
}

// Save config to localStorage
function saveConfig(config: TestConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.warn("[TestConfig] Failed to save config:", error);
  }
}

// Singleton state
const config = ref<TestConfig>(loadConfig());

// Persist changes
watch(
  config,
  (newConfig) => {
    saveConfig(newConfig);
  },
  { deep: true }
);

export function useTestConfig() {
  const updateConfig = <K extends keyof TestConfig>(key: K, value: TestConfig[K]) => {
    config.value[key] = value;
  };

  const updateConfigBatch = (updates: Partial<TestConfig>) => {
    config.value = { ...config.value, ...updates };
  };

  const resetConfig = () => {
    config.value = { ...defaultConfig };
  };

  // Computed helper to get assistant props format (for App.vue compatibility)
  const assistantPropsFormat = computed(() => ({
    position: config.value.position,
    token: config.value.token,
    userId: config.value.userId,
    licenseId: config.value.licenseId,
    patientId: config.value.patientId,
    bookingId: config.value.bookingId,
    professionalId: config.value.professionalId,
    pms: config.value.pms,
    config: {
      autoStart: config.value.autoStart,
      featureFlags: [] as string[],
    },
  }));

  return {
    config: readonly(config),
    updateConfig,
    updateConfigBatch,
    resetConfig,
    assistantPropsFormat,
    defaultConfig,
  };
}

// Export position options for UI
export const POSITION_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
] as const;

// Export PMS options for UI
export const PMS_OPTIONS = [{ value: "feegow", label: "Feegow" }] as const;
